import React from "react";
import {
  Plane,
  Drone,
  Compass,
  Book,
  Wrench,
  Headphones,
  Map,
  ShieldCheck,
  GraduationCap,
  Cog,
  Globe,
  Truck,
  ShieldAlert,
} from "lucide-react";

/* ================= TYPES ================= */
export interface CategoryItem {
  id: number;
  name: string;
  icon: React.ReactNode;
  image?: any;
  parentId?: number | null;
}

/* ──────────────────────────────────────────────
   ICON HELPER — keeps icon size consistent
   ────────────────────────────────────────────── */
const sz = 18; // default icon size (px)

/* ──────────────────────────────────────────────
   1️⃣ TOP-LEVEL AVIATION CATEGORIES (1400000xx)
   ────────────────────────────────────────────── */
export const idAviationCategories: CategoryItem[] = [
  { id: 140000001, name: "Pilot Supplies", icon: <Headphones size={sz} /> },
  { id: 140000002, name: "Flight Training & Education", icon: <GraduationCap size={sz} /> },
  { id: 140000003, name: "Aircraft Parts & Maintenance", icon: <Cog size={sz} /> },
  { id: 140000004, name: "Avionics & Instruments", icon: <Compass size={sz} /> },
  { id: 140000005, name: "Drones & UAVs", icon: <Drone size={sz} /> },
  { id: 140000006, name: "Commercial & Ground Operations", icon: <Truck size={sz} /> },
  { id: 140000007, name: "Aviation Safety & Security", icon: <ShieldAlert size={sz} /> },
  { id: 140000008, name: "Aviation Lifestyle & Merchandise", icon: <Plane size={sz} /> },
];

/* ──────────────────────────────────────────────
   2️⃣ SUBCATEGORIES (1400001xx)
   ────────────────────────────────────────────── */
export const idAviationSubcategories: CategoryItem[] = [
  /* Pilot Supplies */
  { id: 140000101, parentId: 140000001, name: "Headsets & Audio", icon: <Headphones size={sz} /> },
  { id: 140000102, parentId: 140000001, name: "Flight Bags", icon: <Plane size={sz} /> },
  { id: 140000103, parentId: 140000001, name: "Navigation Tools", icon: <Map size={sz} /> },
  { id: 140000104, parentId: 140000001, name: "Pilot Apparel", icon: <Plane size={sz} /> },

  /* Flight Training */
  { id: 140000201, parentId: 140000002, name: "Training Books & Manuals", icon: <Book size={sz} /> },
  { id: 140000202, parentId: 140000002, name: "Simulators & Practice Tools", icon: <Compass size={sz} /> },
  { id: 140000203, parentId: 140000002, name: "Charts & Maps", icon: <Map size={sz} /> },

  /* Aircraft Parts */
  { id: 140000301, parentId: 140000003, name: "Aircraft Parts", icon: <Wrench size={sz} /> },
  { id: 140000302, parentId: 140000003, name: "Maintenance Tools", icon: <Wrench size={sz} /> },
  { id: 140000303, parentId: 140000003, name: "Fluids & Consumables", icon: <Cog size={sz} /> },

  /* Avionics */
  { id: 140000401, parentId: 140000004, name: "Navigation Systems", icon: <Compass size={sz} /> },
  { id: 140000402, parentId: 140000004, name: "Communication Radios", icon: <Headphones size={sz} /> },
  { id: 140000403, parentId: 140000004, name: "Flight Instruments", icon: <Plane size={sz} /> },

  /* Drones */
  { id: 140000501, parentId: 140000005, name: "Consumer Drones", icon: <Drone size={sz} /> },
  { id: 140000502, parentId: 140000005, name: "Professional UAVs", icon: <Drone size={sz} /> },
  { id: 140000503, parentId: 140000005, name: "Drone Accessories", icon: <Wrench size={sz} /> },

  /* Ground Ops */
  { id: 140000601, parentId: 140000006, name: "Ground Support Equipment", icon: <Truck size={sz} /> },
  { id: 140000602, parentId: 140000006, name: "Airport Operations Gear", icon: <Globe size={sz} /> },

  /* Safety */
  { id: 140000701, parentId: 140000007, name: "Safety Equipment", icon: <ShieldCheck size={sz} /> },
  { id: 140000702, parentId: 140000007, name: "Emergency & Survival Gear", icon: <ShieldAlert size={sz} /> },

  /* Lifestyle */
  { id: 140000801, parentId: 140000008, name: "Aviation Apparel", icon: <Plane size={sz} /> },
  { id: 140000802, parentId: 140000008, name: "Models & Collectibles", icon: <Plane size={sz} /> },
  { id: 140000803, parentId: 140000008, name: "Gifts & Decor", icon: <Plane size={sz} /> },
];

/* ──────────────────────────────────────────────
   3️⃣ PRODUCT VARIANTS (1400101xx+)
   ────────────────────────────────────────────── */
export const idAviationProducts: CategoryItem[] = [
  /* Headsets */
  { id: 140010101, parentId: 140000101, name: "ANR Pilot Headsets", icon: <Headphones size={sz} /> },
  { id: 140010102, parentId: 140000101, name: "Passive Aviation Headsets", icon: <Headphones size={sz} /> },

  /* Training */
  { id: 140020101, parentId: 140000201, name: "PPL Training Books", icon: <Book size={sz} /> },
  { id: 140020102, parentId: 140000201, name: "ATPL Study Guides", icon: <Book size={sz} /> },

  /* Drones */
  { id: 140050101, parentId: 140000501, name: "Camera Drones", icon: <Drone size={sz} /> },
  { id: 140050301, parentId: 140000503, name: "Drone Batteries & Propellers", icon: <Wrench size={sz} /> },

  /* Safety */
  { id: 140070101, parentId: 140000701, name: "Flight Helmets", icon: <ShieldCheck size={sz} /> },
  { id: 140070201, parentId: 140000702, name: "Emergency Locator Beacons", icon: <ShieldAlert size={sz} /> },
];